import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'
import { useCreateRequest } from '@/domain/requests'
import { useSplitTransactionStore } from '@/store/useSplitTransactionStore'
import { useUserStore } from '@/store/useUserStore'
import { currencyUtils } from '@/utils'

const { formatFiatCurrency, parseAmountToFloat } = currencyUtils

interface RequestResult {
  recipientId: string
  recipientName: string
  success: boolean
  error?: string
}

export const useHandleSplitRequests = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)

  const transaction = useSplitTransactionStore((s) => s.transaction)
  const selectedRecipients = useSplitTransactionStore((s) => s.selectedRecipients)
  const getAmountPerPerson = useSplitTransactionStore((s) => s.getAmountPerPerson)
  const getInitiatorAmount = useSplitTransactionStore((s) => s.getInitiatorAmount)
  const myId = useUserStore((s) => s.userAuthenticated?.user_id)

  const createRequestMutation = useCreateRequest()

  const handleSendSplitRequests = useCallback(async () => {
    if (!transaction) {
      toast.error(t('errors.noTransaction', {
        defaultValue: 'No transaction selected',
      }))
      return false
    }

    if (selectedRecipients.length === 0) {
      toast.error(t('errors.noRecipientsSelected', {
        defaultValue: 'No recipients selected',
      }))
      return false
    }

    setIsLoading(true)
    const results: RequestResult[] = []
    const amountPerPerson = getAmountPerPerson()

    try {
      // Send requests sequentially
      for (const recipient of selectedRecipients) {
        try {
          await createRequestMutation.mutateAsync({
            initiator_user_id: myId || '',
            acceptor_user_id: recipient.id,
            acceptor_phone_number: recipient.phoneNumber,
            request_type: 'send_request',
            extra_info: {
              amount: String(amountPerPerson),
              currency: transaction.amount.currency,
              note: t('translation:splitRequestNote', {
                defaultValue: `Split from ${transaction.amount.amount} ${transaction.amount.currency} transaction`,
              }),
            },
          })

          results.push({
            recipientId: recipient.id,
            recipientName: recipient.name,
            success: true,
          })
        } catch (error) {
          console.error(`Failed to send split request to ${recipient.name}:`, error)
          results.push({
            recipientId: recipient.id,
            recipientName: recipient.name,
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error',
          })
        }
      }

      const successCount = results.filter((r) => r.success).length
      const failureCount = results.filter((r) => !r.success).length
      const initiatorAmount = getInitiatorAmount()

      // Navigate to success screen with results
      const relativeFiatFormatted = formatFiatCurrency({
        fiatCode: transaction.amount.fiat_currency,
        value: Number(transaction.amount.fiat_amount),
        showDecimals: true,
      })

      navigate('/split-request/success', {
        state: {
          results,
          successCount,
          failureCount,
          amountPerPerson,
          initiatorAmount,
          currency: transaction.amount.currency,
          relativeFiatFormatted,
          originalAmount: transaction.amount.amount,
          type: 'split',
        },
      })

      return true
    } catch (error) {
      console.error('Error in handleSendSplitRequests:', error)
      toast.error(t('errors.somethingWrong'))
      return false
    } finally {
      setIsLoading(false)
    }
  }, [
    transaction,
    selectedRecipients,
    myId,
    getAmountPerPerson,
    getInitiatorAmount,
    createRequestMutation,
    t,
    navigate,
  ])

  return {
    handleSendSplitRequests,
    isLoading,
  }
}

